import React, { useEffect, useState } from "react";
import { SearchModelEnum } from "types/qbank/searchModel";
import type { AutoSuggestValue } from "types/autoSuggestValue";
import {
  loadAutoSuggestValues,
  normalize,
} from "utilities/loadAutoSuggestValues";
import AutoSuggestDropdown from "./AutoSuggestDropdown";
import ActiveFilters from "./ActiveFilters";
import AdvancedSearchPanel from "./advanced/AdvancedSearchPanel";
import { SearchFilterLabels } from "types/searchFilterLabels";
import { SearchOperator } from "types/qbank/searchOperator";
import { useApi } from "hooks/api";
import { useFilter } from "hooks/filter";
import { useQuery } from "react-query";
import { MetaData } from "types/qbank/metadata";
import Dialog from "@mui/material/Dialog";
import Transition from "components/Transition";
import { Icon } from "components/ui";

export default function SearchBar() {
  const { getMetaData } = useApi();
  const { searchState, searchDispatch } = useFilter();
  const [inputValue, setInputValue] = useState("");
  const [isActive, setActive] = useState(false);
  const [metaData, setMetaData] = useState<MetaData>({
    categories: {},
    deploymentSites: {},
    propertyTypes: {},
  });
  const [autoSuggestValues, setAutoSuggestValues] = useState<
    AutoSuggestValue[]
  >([]);
  const [currentlySuggestedValues, setCurrentlySuggestedValues] = useState<
    AutoSuggestValue[]
  >([]);
  const dropdownFocusRef: React.RefObject<HTMLAnchorElement> =
    React.createRef();
  const searchBarFocusRef: React.RefObject<HTMLInputElement> =
    React.createRef();

  function toggleAdvancedSearchPanel() {
    setActive(!isActive);
  }

  function clearSearchInput() {
    setInputValue("");
    setTimeout(() => searchBarFocusRef.current?.focus(), 1);
  }

  function addFreetextValue() {
    if (inputValue.trim() !== "") {
      searchDispatch({
        type: "ADD_FILTER",
        payload: {
          value: inputValue,
          label: "FreeText",
          criteria: SearchModelEnum.freeText,
        },
      });
    }
    clearSearchInput();
  }

  const { isLoading, isError, data } = useQuery("metaData", getMetaData, {
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!isLoading && !isError && data) {
      setMetaData(data);
      //Parse metadata into autosuggest values
      if (Object.keys(data).length) {
        setAutoSuggestValues(loadAutoSuggestValues(data));
      }
    }
  }, [data, isError, isLoading]);

  useEffect(() => {
    function autoSuggest(input: string): AutoSuggestValue[] {
      const normalizedInputValue = normalize(input);

      let matchedValues: AutoSuggestValue[] = [];

      if (inputValue.length > 2 && autoSuggestValues.length) {
        matchedValues = autoSuggestValues.filter(
          (value) =>
            value.normalizedValue &&
            value.normalizedValue.indexOf(normalizedInputValue) > -1
        );
      }

      if (parseInt(input).toString() === input) {
        //Add numeric options
        matchedValues.push({
          searchCriteria: SearchModelEnum.mediaIds,
          searchOperator: SearchOperator.CONTAINS_ANY,
          label: SearchFilterLabels[SearchModelEnum.mediaIds],
          labelValue: input,
          value: input,
        });
        matchedValues.push({
          searchCriteria: SearchModelEnum.objectIds,
          searchOperator: SearchOperator.CONTAINS_ANY,
          label: SearchFilterLabels[SearchModelEnum.objectIds],
          labelValue: input,
          value: input,
        });
      }

      //Always add filename & title
      matchedValues.push({
        searchCriteria: SearchModelEnum.fileName,
        searchOperator: SearchOperator.LIKE,
        label: SearchFilterLabels[SearchModelEnum.fileName],
        labelValue: input,
        value: input,
      });
      matchedValues.push({
        searchCriteria: SearchModelEnum.name,
        searchOperator: SearchOperator.LIKE,
        label: SearchFilterLabels[SearchModelEnum.name],
        labelValue: input,
        value: input,
      });

      return matchedValues;
    }

    //Find autosuggest values while typing
    setCurrentlySuggestedValues(autoSuggest(inputValue));
  }, [inputValue, autoSuggestValues]);

  if (isError) {
    return null;
  }

  return (
    <>
      <div className="field has-addons is-marginless">
        <div className="search-bar control has-icons-right">
          <div className="field is-marginless">
            <div className="control">
              <div
                className={`dropdown ${inputValue.length ? "is-active" : ""}`}
              >
                <div className="dropdown-trigger">
                  <input
                    ref={searchBarFocusRef}
                    className="input"
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    tabIndex={0}
                    onKeyUp={(e) => {
                      if (e.code === "Enter" || e.code === "NumpadEnter") {
                        addFreetextValue();
                      } else if (e.code === "ArrowDown") {
                        dropdownFocusRef.current?.focus();
                      }
                    }}
                  />
                </div>

                <div className="dropdown-menu">
                  {currentlySuggestedValues.length > 0 && (
                    <AutoSuggestDropdown
                      focusRef={dropdownFocusRef}
                      values={currentlySuggestedValues}
                      onClose={clearSearchInput}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {searchState.currentCategory.propertyRequests.length > 0 ? (
            <div
              className="icon is-small is-right toggle-advanced-search-panel"
              onClick={toggleAdvancedSearchPanel}
            >
              <Icon name="sliders-h" />
            </div>
          ) : null}
        </div>

        <div className="control">
          <button
            disabled={inputValue.trim() === ""}
            className="button is-primary"
            onClick={addFreetextValue}
          >
            <Icon name="search" />
          </button>
        </div>
      </div>

      <ActiveFilters />

      <Dialog
        fullScreen
        open={isActive}
        onClose={() => setActive(false)}
        TransitionComponent={Transition}
      >
        <AdvancedSearchPanel
          active={true}
          closePanel={() => setActive(false)}
          propertyTypes={metaData.propertyTypes}
        />
      </Dialog>
    </>
  );
}
